import { z } from "zod";
import {
  completionItemsToTaskKeys,
  completionSelectionSchema,
} from "./completion-picker";
import {
  WEBDOC_LAB_ORDER_UNCONFIRMED,
  webdocControlSchema,
  webdocLabOrderConfirmed,
} from "./prescription-choice";

// CAR-15 — lab orders + lab-result visualisation. The curated panel
// (analyte list, units, female reference ranges) is owned by the API in
// code; the client just renders what `/patient-api/v1/labs` returns.
// `bucket` is the pre-computed reference-range zone — clients don't
// re-implement the "gröna zoner" logic.

export const labReferenceBucketSchema = z.enum([
  "low",
  "in_range",
  "high",
  "unknown",
]);
export type LabReferenceBucket = z.infer<typeof labReferenceBucketSchema>;

// TES-1557 (Ö-747) — the doctor who reviewed and published what she reads,
// as the server knows him from the publish itself: never the responsible
// doctor, never a guess from the care relationship. Emelie 2026-10-21:
// "ingen automatisk attribution enbart från vårdrelation". `avatarMediaKey`
// follows careTeamDoctorSchema: his own portrait, else the tenant's doctor
// slot, null when neither exists.
export const labPublisherSchema = z.object({
  employeeId: z.string(),
  displayName: z.string(),
  avatarMediaKey: z.string().nullable(),
});
export type LabPublisher = z.infer<typeof labPublisherSchema>;

export const labAnalyteSchema = z.object({
  analyteKey: z.string(),
  // i18n key, e.g. 'labs.analytes.hba1c'
  displayKey: z.string(),
  unit: z.string(),
  category: z.string(),
  value: z.number().nullable(),
  valueText: z.string().nullable(),
  observedAt: z.string().nullable(),
  refLow: z.number().nullable(),
  refHigh: z.number().nullable(),
  axisMin: z.number(),
  axisMax: z.number(),
  precision: z.number(),
  bucket: labReferenceBucketSchema,
  // TES-201: the doctor's patient-facing comment for this analyte. Non-null
  // only on analytes belonging to the latest SENT order (feedbackSentAt set);
  // null otherwise — feedback stays hidden until the doctor approves & sends.
  comment: z.string().nullable(),
  // TES-1557 — who published `comment`: null when there is no comment, or
  // when nobody can be named for certain (an order published before the
  // publisher was recorded). Optional: absent from an older API, and an
  // older app ignores it, so the shape needs no contract date.
  commentPublishedBy: labPublisherSchema.nullable().optional(),
});
export type LabAnalyte = z.infer<typeof labAnalyteSchema>;

// TES-201: the overall doctor feedback for the latest order whose feedback has
// been sent. Present (non-null on the response) only after the doctor approves
// & sends; null gates the soft-green "Din läkare har gått igenom dina provsvar"
// card and all per-analyte comments off in the mobile UI.
export const patientLabFeedbackSchema = z.object({
  orderId: z.string().uuid(),
  sentAt: z.string(),
  summary: z.string(),
  // The order's latest result observedAt — drives the "Provsvar" header date.
  sampleDate: z.string(),
  // TES-1370 (Ö-582) — "Delsvar ska markeras tydligt som ofullständiga": true
  // when panel values of this order have not been published to her yet
  // (the lab has not sent them, or the doctor has not published them), and
  // which. The app names them from its own analyte i18n table. Optional so
  // an older API response still parses (absent = complete).
  partial: z.boolean().optional(),
  missingAnalyteKeys: z.array(z.string()).optional(),
  // TES-1557 (Ö-747) — the doctor who stands behind this feedback, "med
  // datum/version": `sentAt` is the date of his (latest) publish and
  // `publishedVersion` which publish it is (1, 2, ...). Both null when
  // nobody can be named for certain; the app then names no one. Optional,
  // additive: an older app ignores them.
  publishedBy: labPublisherSchema.nullable().optional(),
  publishedVersion: z.number().int().nullable().optional(),
});
export type PatientLabFeedback = z.infer<typeof patientLabFeedbackSchema>;

export const patientLabsResponseSchema = z.object({
  panelKey: z.string(),
  panelVersion: z.number().int(),
  latestOrder: z
    .object({
      id: z.string().uuid(),
      status: z.string(),
      orderedAt: z.string(),
    })
    .nullable(),
  analytes: z.array(labAnalyteSchema),
  // TES-201: null until the doctor has approved & sent feedback for an order.
  feedback: patientLabFeedbackSchema.nullable(),
});
export type PatientLabsResponse = z.infer<typeof patientLabsResponseSchema>;

export const labAnalyteHistoryPointSchema = z.object({
  value: z.number().nullable(),
  valueText: z.string().nullable(),
  observedAt: z.string(),
  // TES-201: the reference-range zone for this point, computed against the
  // panel version its order was placed with — lets the trend chart colour each
  // point against its zone without re-implementing the bucketing logic.
  bucket: labReferenceBucketSchema,
});
export type LabAnalyteHistoryPoint = z.infer<
  typeof labAnalyteHistoryPointSchema
>;

// TES-890 (Ö-185 ✅ 2026-09-23) — "Provsvar granskat", and what the doctor
// concluded.
//
// Emelie, verbatim:
// "Kirill markerar Provsvar granskat. Han väljer ett av tre utfall: godkänt underlag · komplettering krävs · nytt prov krävs. Handlingen loggas (vem, när, vilket utfall)."
//
// WHY THIS IS NOT JUST A TIMESTAMP. `LabOrder.reviewedAt` used to carry two
// facts at once — that a doctor had looked, and that what he saw was good
// enough to treat on — because approval was the only outcome the code could
// express. With three, those come apart: a result Kirill reviewed and found
// insufficient has been reviewed and must NOT open the activation gate. So
// the timestamp says "looked at" and the outcome says "and concluded what".
//
// No polling. TES-475 Ö-185 closes that door for MVP:
// "Kirill får notifiering i Webdoc. Ingen separat pollingrutin byggs i Ella."
// Results arrive outside the system; this is only the acknowledgement of them.
export const LAB_REVIEW_OUTCOMES = [
  // "Godkänt underlag" — the evidence carries a treatment decision.
  "approved",
  // "Komplettering krävs" — something specific is missing.
  "completion_required",
  // "Nytt prov krävs" — the sample itself has to be redone.
  "new_sample_required",
] as const;
export const labReviewOutcomeSchema = z.enum(LAB_REVIEW_OUTCOMES);
export type LabReviewOutcome = z.infer<typeof labReviewOutcomeSchema>;

// TES-906 (Ö-227) — who pays for the re-test.
//
// Emelie 2026-09-23, verbatim: at "Nytt prov krävs" the doctor must pick one
// of two "utan förvalt standardvärde". So this enum has no default anywhere,
// and the schema below REFUSES a `new_sample_required` without it. A server
// that picked would be deciding whether a patient pays, which is the one thing
// the answer says a human must do.
export const LAB_SAMPLE_COSTS = [
  // "Omprov ingår" — no new payment. The re-test still gets its own case.
  "covered",
  // "Nytt/utökat provpaket" — a payment task is created first, gating the
  // sample exactly as CP-06 Variant B does for a first blood test.
  "charged",
] as const;
export const labSampleCostSchema = z.enum(LAB_SAMPLE_COSTS);
export type LabSampleCost = z.infer<typeof labSampleCostSchema>;

// The body of POST …/lab-orders/:orderId/approve. `outcome` defaults to
// `approved` so the existing route keeps its meaning for a caller that sends
// nothing — the endpoint was approval-only before this, and a client that has
// not been updated must not silently start recording a different conclusion.
//
// `new_sample_required` is the one outcome that opens a case, so it is the one
// that has to say what the case contains. Both extra fields are required for
// it and forbidden on the others: a `sampleCost` attached to `approved` would
// be a charge decision about a re-test nobody ordered.
//
// `requestedTasks` is EXHAUSTIVE, the same rule as the transfer review's
// (TES-763): only what she names appears. It excludes `lab_payment`
// deliberately — whether the patient pays is `sampleCost`'s answer, not a
// task the doctor ticks, and offering it in both places is how the two would
// come to disagree.
// TES-950 — the shared completion picker on the lab review. The completion
// outcome used to record the conclusion and open nothing, so the patient was
// never asked for what the doctor found missing. With `completion` it opens a
// case with exactly the chosen items, like the other two entry points.
//
// A new blood test is the one item that is not a plain komplettering: it is
// "Nytt prov krävs", with Ö-227's cost question. So `new_blood_test` among the
// items requires that outcome, and the admin derives the outcome from the
// selection rather than asking twice.
// TES-1181 (TES-1180 point 5) — A-02:
// "Patienten får inte få uppgiften att lämna blodprov innan denna bekräftelse finns."
// A lab review whose outcome is "Nytt prov krävs" asks for a new sample
// whatever else it carries, so the outcome alone decides the tick.
export function refineNewSampleLabOrder(
  v: {
    outcome: LabReviewOutcome;
    webdocControl?: z.infer<typeof webdocControlSchema>;
  },
  ctx: z.RefinementCtx,
): void {
  if (
    v.outcome === "new_sample_required" &&
    !webdocLabOrderConfirmed(v.webdocControl)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["webdocControl", "labOrderDoneInWebdoc"],
      message: WEBDOC_LAB_ORDER_UNCONFIRMED,
    });
  }
}

export const reviewLabOrderBodySchema = z
  .object({
    outcome: labReviewOutcomeSchema.default("approved"),
    sampleCost: labSampleCostSchema.optional(),
    requestedTasks: z.array(z.string().min(1)).optional(),
    reason: z.string().trim().max(2000).optional(),
    completion: completionSelectionSchema.optional(),
    // TES-1181 (TES-1180 point 5) — "Nytt prov krävs" gives her a blood-test
    // task, so the remiss tick is required here as on every other route.
    webdocControl: webdocControlSchema.optional(),
  })
  .superRefine((v, ctx) => {
    refineNewSampleLabOrder(v, ctx);
    if (v.completion) {
      if (v.outcome === "approved") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["completion"],
          message: "completion_not_applicable",
        });
      }
      if (v.requestedTasks && v.requestedTasks.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["requestedTasks"],
          message: "completion_excludes_requested_tasks",
        });
      }
      const newSample = v.completion.items.includes("new_blood_test");
      if (v.outcome === "completion_required" && newSample) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["outcome"],
          message: "new_blood_test_requires_new_sample_required",
        });
      }
      if (v.outcome === "new_sample_required" && !v.sampleCost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sampleCost"],
          message: "sample_cost_required",
        });
      }
      if (v.outcome === "completion_required" && v.sampleCost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sampleCost"],
          message: "sample_cost_not_applicable",
        });
      }
      return;
    }
    if (v.outcome === "new_sample_required") {
      if (!v.sampleCost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sampleCost"],
          message: "sample_cost_required",
        });
      }
      // An empty list is a doctor who named nothing, and TES-844 is what
      // happens when a request is recorded that nothing renders. Refuse it
      // here rather than open a case with no tasks in it.
      if (!v.requestedTasks || v.requestedTasks.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["requestedTasks"],
          message: "requested_tasks_required",
        });
      }
      return;
    }
    if (v.sampleCost) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sampleCost"],
        message: "sample_cost_not_applicable",
      });
    }
    if (v.requestedTasks && v.requestedTasks.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["requestedTasks"],
        message: "requested_tasks_not_applicable",
      });
    }
  });
export type ReviewLabOrderBody = z.infer<typeof reviewLabOrderBodySchema>;

// TES-950 — the CP-06 tasks a lab review opens. From the picker when the
// doctor used it; otherwise the pre-picker `requestedTasks` of the new-sample
// outcome. `lab_payment` is never among them: whether she pays is `sampleCost`
// (added by the case lookup), not a task anyone ticks.
export function labReviewRequestedTasks(body: ReviewLabOrderBody): string[] {
  if (body.completion) {
    return completionItemsToTaskKeys(body.completion.items, {
      labPayment: false,
    });
  }
  return body.outcome === "new_sample_required"
    ? (body.requestedTasks ?? [])
    : [];
}
